import { Resume } from './resume';
import { Return } from './return';
import type { Product } from './types';

/** What the sign-in screen can truthfully say about where signing in leads */
export type Course =
	/** A product this installation registers, by its name: the hand-off it interrupted, or a return it asked for */
	| { kind: 'product'; name: string; product: Product }
	/**
	 * An application's request to connect to a product, interrupted: the product is named when one of its
	 * resources is the one asked for. The application itself is never named here, because its name is its
	 * own claim, which only the consent shows, marked unverified
	 */
	| { kind: 'connection'; name?: string; product?: Product }
	/** A screen of this interface the person was on their way to */
	| { kind: 'here' }
	/** An invitation waiting to be accepted */
	| { kind: 'invitation' }
	/** The address asked for a destination this interface does not follow: it is ignored, and said so */
	| { kind: 'ignored' };

/**
 * Where a sign-in is going, read from the sign-in address before anyone signs in (settled contract S06:
 * the destination is stated, and an unsafe one is ignored and said so).
 *
 * The address is written by whoever sent the person here, so nothing in it is named unchecked: a
 * product is named only when this installation registers it, and by the name it registers, never by
 * the identifier that arrived. A product's return address is still validated by the service after
 * signing in (`Return`); this only decides what may be said beforehand. An application connecting to a
 * product is said as such, naming the product its resource belongs to and never the application, whose
 * name nobody has checked.
 */
export class Destination {
	#parameters: URLSearchParams;
	#authority: string;

	constructor(parameters: URLSearchParams, authority: string) {
		this.#parameters = parameters;
		this.#authority = authority;
	}

	/**
	 * @param products The products this installation registers, when they have been read
	 * @returns What to say, or undefined when there is nothing to say or not enough is known yet
	 */
	course(products: Product[] | undefined): Course | undefined {
		const to = this.#parameters.get('to');
		const product = this.#parameters.get(Return.PRODUCT);

		// The service's own hand-off, interrupted: it validated the product, or the application connecting
		// and its redirect, before sending the person here
		const handoff = Resume.handoff(to, this.#authority);
		if (handoff) {
			const asked = new URL(handoff).searchParams;
			if (Destination.connecting(asked)) return this.#connection(asked.get('resource'), products);
			return this.#named(asked.get(Return.PRODUCT), products, false);
		}

		if (Return.requested(this.#parameters)) return this.#named(product, products, true);
		if (product) return { kind: 'ignored' };

		if (!to) return undefined;
		const path = Resume.path(to);
		if (!path) return { kind: 'ignored' };
		return path.startsWith('/invitations/') ? { kind: 'invitation' } : { kind: 'here' };
	}

	/**
	 * Whether an authorization address is an application's OAuth request rather than a product's
	 * hand-off: the service tells them apart by the same parameters
	 */
	static connecting(parameters: URLSearchParams): boolean {
		return parameters.has('client_id') || parameters.has('response_type');
	}

	/** The product whose resource an application asked for, compared as given or without a trailing slash */
	#connection(resource: string | null, products: Product[] | undefined): Course | undefined {
		if (!products) return undefined;
		const bare = (value: string) => value.replace(/\/+$/, '');
		const found = resource ? products.find(entry => entry.resources?.some(each => bare(each.uri) === bare(resource))) : undefined;
		return found ? { kind: 'connection', name: found.name, product: found } : { kind: 'connection' };
	}

	#named(id: string | null, products: Product[] | undefined, strict: boolean): Course | undefined {
		if (!products) return undefined;
		const found = products.find(entry => entry.id === id);
		if (found) return { kind: 'product', name: found.name, product: found };
		return strict ? { kind: 'ignored' } : undefined;
	}
}
